<script setup lang="ts">
import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from '@/composables/useLocale';
import PerformanceLevelVisualBadge from '@/core/badges/PerformanceLevelVisualBadge.vue';
import SchoolGradeModifierSymbol from './SchoolGradeModifierSymbol.vue';
import { getSchoolGradePresentation } from 'common/utils/school.grade.utils';
import { getSchoolGradeName } from './schoolGradePresentation.utils';
const props = withDefaults(
  defineProps<{
    configuration: ISchoolGradeDisplayConfiguration | null | undefined;
    value: number;
    name?: string | null;
    variant?: 'compact' | 'default' | 'report' | 'reportDense';
  }>(),
  { name: undefined, variant: 'default' },
);
const { t } = useI18n();
const { formattingLocale } = useLocale();
const presentation = computed(() =>
  props.configuration ? getSchoolGradePresentation(props.value, props.configuration, formattingLocale.value) : null,
);
const name = computed(() => props.name ?? getSchoolGradeName(props.value, props.configuration?.gradeLevels ?? [], t));
const textScale = computed(() => `${Math.min(1, 2.8 / (presentation.value?.text.length ?? 1))}em`);
</script>
<template>
  <PerformanceLevelVisualBadge
    v-if="presentation"
    class="relative font-semibold text-tint"
    :class="{
      'size-icon-md text-footer': variant === 'compact',
      'size-icon-button text-body': variant === 'default',
      'text-heading': variant === 'report' || variant === 'reportDense',
    }"
    :variant="variant"
    :title="name"
    role="img"
    :aria-label="`${presentation.label} ${name}`"
  >
    <span class="whitespace-nowrap text-center leading-none" :style="{ fontSize: textScale }" aria-hidden="true">{{
      presentation.text
    }}</span>
    <span
      v-if="presentation.modifier"
      class="absolute right-0 top-0 flex size-[40%] min-size-icon-smd -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full text-white"
      :class="presentation.modifier === 1 ? 'bg-success' : 'bg-error'"
      aria-hidden="true"
    >
      <SchoolGradeModifierSymbol :modifier="presentation.modifier" class="size-1/2" />
    </span>
  </PerformanceLevelVisualBadge>
</template>
